/**
 * The one toast a project removal owns, from "started" to a terminal outcome.
 *
 * WHY THIS MODULE EXISTS — two live defects, one missing thing.
 *
 * (A) STALE ERROR TOAST. Every removal call site used to do a bare
 *     `toastManager.add({type:"error", ...})` on failure and DISCARD the id it
 *     returns. Nothing could reference that toast again, so no later outcome
 *     could retract it: a "Failed to remove …" card sat on screen long after
 *     that very project had been removed successfully. Base UI's own 5s timer
 *     does not save it either — the store pauses every timer while the window
 *     is unfocused (`expandedOrOutOfFocus`), which a background tab is enough
 *     to trigger indefinitely. The fix is not a shorter TTL, it is ADDRESSING:
 *     one toast per project key, tracked, so a later outcome can replace it.
 *
 * (C) SLOW REMOVAL LOOKED SILENT. A clean removal produced no toast AT ALL —
 *     only the failure branch spoke. Provider cleanup may take several minutes,
 *     so immediate feedback complements the durable catalog overlay.
 *
 * SHAPE. Keyed by `projectKey` at MODULE scope, deliberately: the sidebar row
 * unmounts the instant a removal succeeds, so a ref inside the row's hook is
 * destroyed at precisely the moment the terminal toast is needed. The map also
 * lets the bulk "remove all" path settle a project it never individually
 * opened, which is how a stale failure gets retracted in practice.
 *
 * Terminal states are `close(old) + add(new)` rather than `update(old)`. Base
 * UI's `updateToast` silently no-ops when the toast is gone or already ending
 * (see `store.js`: `if (!prevToast) return;`), so an `update`-only settle can
 * lose the outcome entirely if the user swiped the loading toast away. Adding
 * a fresh terminal toast cannot fail that way. Progress steps DO use `update`
 * — losing one is harmless, and updating in place is what keeps the stack from
 * growing a card per step.
 */

import { toastManager } from "../ui/toast";

type ToastId = ReturnType<typeof toastManager.add>;

/**
 * Terminal toasts expire; the in-flight one does not.
 *
 * A `type: "loading"` toast is never scheduled for auto-close by Base UI, and
 * `timeout: 0` says so explicitly at the call site so the intent survives a
 * future type change. The terminal timeouts are finite so a removal card can
 * never become permanent furniture — the failure gets longer because it is the
 * one the user may need to read and copy.
 */
const IN_FLIGHT_TOAST_TIMEOUT_MS = 0;
const REMOVED_TOAST_TIMEOUT_MS = 5_000;
const FAILED_TOAST_TIMEOUT_MS = 15_000;

/** projectKey -> the toast that project currently owns, terminal or not. */
const projectRemovalToastIds = new Map<string, ToastId>();

interface ProjectRemovalToastTarget {
  /** `SidebarProjectSnapshot.projectKey` — stable across the row's lifetime. */
  readonly projectKey: string;
  readonly projectName: string;
}

/**
 * Retire whatever this project currently has on screen and forget it.
 *
 * This is the retraction defect (A) needed: closing is unconditional and
 * idempotent — `closeToast` on an unknown id is a no-op in the store — so a
 * caller never has to know whether the previous card was a spinner, an error,
 * or already dismissed by the user.
 */
function retireProjectRemovalToast(projectKey: string): void {
  const existingToastId = projectRemovalToastIds.get(projectKey);
  if (existingToastId === undefined) return;
  projectRemovalToastIds.delete(projectKey);
  toastManager.close(existingToastId);
}

/**
 * Retract this project's toast and add nothing in its place.
 *
 * For the bulk "remove all" surface, which reports ONE aggregate outcome for N
 * projects: it still has to be able to clear a project's stale individual
 * failure (defect A) without stacking N success cards on top of its own
 * summary. Safe to call for a project that has nothing on screen.
 */
export function clearProjectRemovalToast(projectKey: string): void {
  retireProjectRemovalToast(projectKey);
}

/**
 * A removal has started. Opens the pinned in-flight toast, replacing anything
 * this project left behind — including a failure from an earlier attempt, which
 * is stale the moment a retry begins.
 */
export function openProjectRemovalToast(target: ProjectRemovalToastTarget): void {
  retireProjectRemovalToast(target.projectKey);
  projectRemovalToastIds.set(
    target.projectKey,
    toastManager.add({
      type: "loading",
      title: `Removing "${target.projectName}"…`,
      description: "Starting…",
      timeout: IN_FLIGHT_TOAST_TIMEOUT_MS,
      data: { hideCopyButton: true },
    }),
  );
}

/**
 * A named step of the removal began. Updates the in-flight card in place so the
 * user can see WHICH slow thing is currently slow, rather than a static
 * "Removing…" that is indistinguishable from a hang.
 */
export function reportProjectRemovalStep(
  target: ProjectRemovalToastTarget & { readonly stepLabel: string },
): void {
  const toastId = projectRemovalToastIds.get(target.projectKey);
  if (toastId === undefined) return;
  toastManager.update(toastId, {
    type: "loading",
    title: `Removing "${target.projectName}"…`,
    description: `${target.stepLabel}…`,
    timeout: IN_FLIGHT_TOAST_TIMEOUT_MS,
    data: { hideCopyButton: true },
  });
}

/**
 * The removal succeeded — authoritatively, meaning the project is proven gone
 * from the catalog, not merely that a DELETE was accepted.
 *
 * Retiring first is what retracts a stale failure for this project (defect A)
 * even when the success came from a different surface (the bulk "remove all"
 * path) that never opened a toast of its own.
 */
export function settleProjectRemovalRemoved(target: ProjectRemovalToastTarget): void {
  retireProjectRemovalToast(target.projectKey);
  projectRemovalToastIds.set(
    target.projectKey,
    toastManager.add({
      type: "success",
      title: `Removed "${target.projectName}"`,
      timeout: REMOVED_TOAST_TIMEOUT_MS,
      data: { hideCopyButton: true },
    }),
  );
}

/** The removal failed, and the row is still on screen. */
export function settleProjectRemovalFailed(
  target: ProjectRemovalToastTarget & { readonly description: string },
): void {
  retireProjectRemovalToast(target.projectKey);
  projectRemovalToastIds.set(
    target.projectKey,
    toastManager.add({
      type: "error",
      title: `Failed to remove "${target.projectName}"`,
      description: target.description,
      timeout: FAILED_TOAST_TIMEOUT_MS,
    }),
  );
}
